import React from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ParticipantSelector from "./ParticipantSelector";
import type { Participant } from "../../../types";
import { getDeviceType } from "../../../utils/deviceType";
import ParticipantCoverageFilters, {
  DatePreset,
} from "./ParticipantCoverageFilters";
import ParticipantCoverageSummaryTable from "./ParticipantCoverageSummaryTable";
import MissingDataAnalysisCard from "./MissingDataAnalysisCard";
import MetricCoverageHeatmapCard from "./MetricCoverageHeatmapCard";
import MetricStatsCard from "./MetricStatsCard";
import ExportReportModal, { type ExportModule } from "./ExportReportModal";
import {
  DataCoverageService,
  ParticipantCoverageSummaryResponse,
  type MissingDataAnalysisResponse,
  type MetricStatsResponse,
} from "../../../services/dataCoverageService";
import { dataCoverageReportExportService } from "../../../services/dataCoverageReportExportService";
import { htmlReportGenerator } from "../../../utils/dataCoverageHtmlReportGenerator";
import {
  createAndDownloadZip,
  dataUrlToBlob,
} from "../../../utils/dataCoverageZipExport";
import { apiService } from "../../../config/api";

interface ParticipantLevelContentProps {
  studyId: string;
}

interface ParticipantModuleData {
  summaryData: ParticipantCoverageSummaryResponse | null;
  missingDataAnalysis: MissingDataAnalysisResponse | null;
  metricsStats: MetricStatsResponse[];
}

function captureHeatmapPng(chartContainerId: string): string | null {
  const container = document.getElementById(chartContainerId);
  const canvas = container?.querySelector("canvas") as HTMLCanvasElement | null;
  if (!canvas) return null;

  try {
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Failed to capture heatmap PNG:", error);
    return null;
  }
}

const ParticipantLevelContent: React.FC<ParticipantLevelContentProps> = ({
  studyId,
}) => {
  const [selectedParticipant, setSelectedParticipant] =
    React.useState<Participant | null>(null);
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>("7d");
  const [running, setRunning] = React.useState(false);
  const [progressStatus, setProgressStatus] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] =
    React.useState<ParticipantCoverageSummaryResponse | null>(null);
  const [moduleData, setModuleData] = React.useState<ParticipantModuleData>({
    summaryData: null,
    missingDataAnalysis: null,
    metricsStats: [],
  });
  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  const inferredDeviceType = React.useMemo<
    "fitbit" | "healthkit" | "both" | "none"
  >(() => {
    if (!selectedParticipant) return "none";
    return getDeviceType(selectedParticipant);
  }, [selectedParticipant]);

  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, []);

  const handleParticipantChange = (participant: Participant | null) => {
    setSelectedParticipant(participant);
    setResult(null);
    setError(null);
    setProgressStatus("");
    setModuleData({
      summaryData: null,
      missingDataAnalysis: null,
      metricsStats: [],
    });
  };

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  const runCoverage = async () => {
    if (!selectedParticipant) return;
    if (!fromDate || !endDate) {
      setError("Please select both from and end dates.");
      return;
    }

    try {
      setRunning(true);
      setError(null);
      setProgressStatus("Fetching from TimescaleDB...");
      setResult(null);

      const token = await apiService.getAuthToken();
      if (!token) {
        setError("Sign in to run coverage summary.");
        return;
      }

      const response = await DataCoverageService.getParticipantSummaryStats(
        selectedParticipant.participantId,
        {
          fromDate,
          endDate,
          preset,
          refreshIfMissing: true,
        },
        token,
      );

      if (response.success && (response as any).data) {
        setResult((response as any).data);
        setProgressStatus((response as any).data.fetch.statusMessage);
        setModuleData((prev) => ({
          ...prev,
          summaryData: (response as any).data,
        }));
      } else {
        setError("Failed to run data coverage summary.");
      }
    } catch (err: any) {
      setError(err?.message || "Error while running data coverage summary.");
    } finally {
      setRunning(false);
    }
  };

  const handleMissingDataChange = (
    data: MissingDataAnalysisResponse | null,
  ) => {
    setModuleData((prev) => ({ ...prev, missingDataAnalysis: data }));
  };

  const handleMetricsStatsChange = (stats: MetricStatsResponse[]) => {
    setModuleData((prev) => ({ ...prev, metricsStats: stats }));
  };

  const handleExport = async (selectedModuleIds: string[]) => {
    try {
      setIsExporting(true);
      const files: Array<{ name: string; content: string | Blob }> = [];
      const timestamp = new Date().toISOString().slice(0, 10);

      if (!selectedParticipant) return;

      // Export selected modules
      if (selectedModuleIds.includes("summary") && moduleData.summaryData) {
        const summaryJson =
          dataCoverageReportExportService.generateParticipantSummaryJSON(
            moduleData.summaryData,
          );
        summaryJson.metadata.studyId = studyId;
        files.push({
          name: `participant_summary_${timestamp}.json`,
          content: JSON.stringify(summaryJson, null, 2),
        });
      }

      if (
        selectedModuleIds.includes("missing") &&
        moduleData.missingDataAnalysis
      ) {
        const missingJson =
          dataCoverageReportExportService.generateParticipantMissingDataJSON(
            moduleData.missingDataAnalysis,
          );
        missingJson.metadata.studyId = studyId;
        files.push({
          name: `participant_missing_data_${timestamp}.json`,
          content: JSON.stringify(missingJson, null, 2),
        });
      }

      if (
        selectedModuleIds.includes("metrics") &&
        moduleData.metricsStats.length > 0
      ) {
        const metricsJson =
          dataCoverageReportExportService.generateParticipantMetricsStatsJSON(
            moduleData.metricsStats,
            selectedParticipant.participantId,
            selectedParticipant.firstName && selectedParticipant.lastName
              ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`
              : selectedParticipant.participantId,
          );
        metricsJson.metadata.studyId = studyId;
        files.push({
          name: `participant_metrics_stats_${timestamp}.json`,
          content: JSON.stringify(metricsJson, null, 2),
        });
      }

      // Generate HTML report
      const summaryJson = moduleData.summaryData
        ? dataCoverageReportExportService.generateParticipantSummaryJSON(
            moduleData.summaryData,
          )
        : null;
      const missingJson = moduleData.missingDataAnalysis
        ? dataCoverageReportExportService.generateParticipantMissingDataJSON(
            moduleData.missingDataAnalysis,
          )
        : undefined;
      const metricsJson =
        moduleData.metricsStats.length > 0
          ? dataCoverageReportExportService.generateParticipantMetricsStatsJSON(
              moduleData.metricsStats,
              selectedParticipant.participantId,
              selectedParticipant.firstName && selectedParticipant.lastName
                ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`
                : selectedParticipant.participantId,
            )
          : undefined;

      const htmlContent = htmlReportGenerator.generateParticipantReport(
        summaryJson
          ? {
              ...summaryJson,
              metadata: {
                ...summaryJson.metadata,
                studyId,
              },
            }
          : {
              metadata: {
                exportedAt: new Date().toISOString(),
                studyId,
                participantId: selectedParticipant.participantId,
                participantName:
                  selectedParticipant.firstName && selectedParticipant.lastName
                    ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`
                    : selectedParticipant.participantId,
                jsonVersion: "1.0",
              },
              summaryStats: {
                dateRange: {
                  fromDate: "",
                  endDate: "",
                  totalDays: 0,
                },
                deviceType: "none",
                metricsWithData: 0,
                totalMetrics: 0,
                avgCoveragePct: 0,
                logs: [],
              },
              metricCoverage: [],
            },
        missingJson,
        metricsJson,
        captureHeatmapPng("participant-coverage-heatmap-chart") || undefined,
      );

      const heatmapImageDataUrl = captureHeatmapPng(
        "participant-coverage-heatmap-chart",
      );
      if (heatmapImageDataUrl) {
        const pngBlob = dataUrlToBlob(heatmapImageDataUrl);
        if (pngBlob) {
          files.push({
            name: `participant_coverage_heatmap_${timestamp}.png`,
            content: pngBlob,
          });
        }
      }

      files.push({
        name: `participant_report_${timestamp}.html`,
        content: htmlContent,
      });

      // Create and download ZIP
      const participantName =
        selectedParticipant.firstName && selectedParticipant.lastName
          ? `${selectedParticipant.firstName}-${selectedParticipant.lastName}`
          : selectedParticipant.participantId;
      await createAndDownloadZip(
        files,
        `participant-report-${participantName}-${timestamp}.zip`,
      );
    } catch (error) {
      console.error("Export error:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const modules: ExportModule[] = [
    {
      id: "summary",
      label: "Participant Coverage Summary",
      hasData: !!moduleData.summaryData,
      description: "Overall coverage statistics and metrics overview",
    },
    {
      id: "missing",
      label: "Missing Data Analysis",
      hasData: !!moduleData.missingDataAnalysis,
      description: "Missing date ranges and data gaps analysis",
    },
    {
      id: "metrics",
      label: "Metrics Statistics",
      hasData: moduleData.metricsStats.length > 0,
      description: "Detailed metrics statistics with aggregations",
    },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <ParticipantSelector
        studyId={studyId}
        selectedParticipant={selectedParticipant}
        onParticipantSelect={handleParticipantChange}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={() => setExportModalOpen(true)}
          sx={{
            backgroundColor: "#8C1515",
            "&:hover": { backgroundColor: "#B33A3A" },
            px: 3,
            py: 1,
            fontWeight: 600,
          }}
        >
          Export Report
        </Button>
      </Box>

      {selectedParticipant ? (
        <Stack spacing={3} sx={{ mt: 3 }}>
          <ParticipantCoverageFilters
            disabled={running}
            fromDate={fromDate}
            endDate={endDate}
            preset={preset}
            onFromDateChange={setFromDate}
            onEndDateChange={setEndDate}
            onPresetChange={applyPreset}
            onRun={runCoverage}
            running={running}
          />

          {running && (
            <Paper sx={{ p: 2 }}>
              <Stack spacing={1}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary">
                  {progressStatus}
                </Typography>
              </Stack>
            </Paper>
          )}

          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              {result?.fetch?.failedFitbitRefreshRanges &&
                result.fetch.failedFitbitRefreshRanges.length > 0 && (
                  <Alert severity="warning">
                    <Typography variant="body2">
                      Failed to refresh Fitbit for the following date ranges:{" "}
                      {result.fetch.failedFitbitRefreshRanges
                        .map(
                          (range) => `${range.startDate} to ${range.endDate}`,
                        )
                        .join(", ")}
                    </Typography>
                  </Alert>
                )}

              {/* Results Section */}
              {result && !running && (
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Results
                  </Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Avg Coverage
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {result.summary.avgCoveragePct.toFixed(1)}%
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        First Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {result.metrics.length > 0
                          ? result.metrics
                              .filter((m) => m.firstDate)
                              .sort(
                                (a, b) =>
                                  new Date(a.firstDate!).getTime() -
                                  new Date(b.firstDate!).getTime(),
                              )[0]?.firstDate || "-"
                          : "-"}
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Last Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {result.metrics.length > 0
                          ? result.metrics
                              .filter((m) => m.lastDate)
                              .sort(
                                (a, b) =>
                                  new Date(b.lastDate!).getTime() -
                                  new Date(a.lastDate!).getTime(),
                              )[0]?.lastDate || "-"
                          : "-"}
                      </Typography>
                    </Paper>
                  </Stack>

                  <ParticipantCoverageSummaryTable rows={result.metrics} />
                </Stack>
              )}
            </Stack>
          </Paper>

          <MissingDataAnalysisCard
            participantId={selectedParticipant.participantId}
            selectedParticipant={selectedParticipant}
            onDataChange={handleMissingDataChange}
          />

          <MetricStatsCard
            participantId={selectedParticipant.participantId}
            deviceType={inferredDeviceType}
            selectedParticipant={selectedParticipant}
            onStatsChange={handleMetricsStatsChange}
          />

          <MetricCoverageHeatmapCard
            participantId={selectedParticipant.participantId}
            selectedParticipant={selectedParticipant}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      ) : (
        <Paper
          sx={{
            p: 3,
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography variant="h6" color="textSecondary">
              Select a Participant
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Choose a participant from the dropdown above to view their data
              coverage metrics.
            </Typography>
          </Stack>
        </Paper>
      )}

      <ExportReportModal
        open={exportModalOpen}
        modules={modules}
        onExport={handleExport}
        onClose={() => setExportModalOpen(false)}
        isExporting={isExporting}
      />
    </Box>
  );
};

export default ParticipantLevelContent;
